import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';

import { API_URL } from '../../injection-tokens';
import {
    ApiResponse,
    CategoryGroupInformation,
    FpcRelationship,
    IndividualApiError,
    IndividualNote,
    IndividualBySourceIdResponse,
    IndividualUdfField,
    ProblemDetails,
    UdfInformation,
    CreateRelationship
} from './individual-api.types';

@Injectable({ providedIn: 'root' })
export class IndividualApiService {
    private readonly http = inject(HttpClient);
    private readonly apiUrl = `${inject(API_URL)}/Individual`;
    private readonly loggedInUdfEndpoint = this.buildLoggedInUdfEndpoint();
    private readonly loggedInGroupEndpoint = this.buildLoggedInGroupEndpoint();
    private readonly approveFpcIndividualEndpoint = this.buildApproveFpcIndividualEndpoint();
    private readonly rejectFpcIndividualEndpoint = this.buildRejectFpcIndividualEndpoint();
    private readonly deleteIndividualAndEntityTypeLinksByLnkGuidEndpoint =
        this.buildDeleteIndividualAndEntityTypeLinksByLnkGuidEndpoint();
    private readonly updateFPCRelationshipByLnkGuidAndIndGuidEndpoint =
        this.buildUpdateFPCRelationshipByLnkGuidAndIndGuidEndpoint();

    getLoggedInUdf(): Observable<UdfInformation[]> {
        return this.http.get<UdfInformation[] | null>(this.loggedInUdfEndpoint).pipe(
            map((response) => response ?? []),
            catchError((error: HttpErrorResponse) => {
                const problemDetails = this.getProblemDetails(error.error);
                const individualApiError: IndividualApiError = {
                    status: error.status,
                    message: this.getErrorMessage(error, problemDetails),
                    problemDetails,
                    originalError: error,
                };

                return throwError(() => individualApiError);
            }),
        );
    }

    getLoggedInGroups(): Observable<CategoryGroupInformation[]> {
        return this.http.get<CategoryGroupInformation[] | null>(this.loggedInGroupEndpoint).pipe(
            map((response) => response ?? []),
            catchError((error: HttpErrorResponse) => {
                const problemDetails = this.getProblemDetails(error.error);
                const individualApiError: IndividualApiError = {
                    status: error.status,
                    message: this.getErrorMessage(error, problemDetails, 'LoggedIn Group'),
                    problemDetails,
                    originalError: error,
                };

                return throwError(() => individualApiError);
            }),
        );
    }

    getBySourceId(sourceId: string): Observable<IndividualBySourceIdResponse> {
        return this.http.get<Record<string, unknown> | null>(this.buildBySourceIdEndpoint(sourceId)).pipe(
            map((response) => this.projectBySourceIdResponse(response)),
            catchError((error: HttpErrorResponse) => {
                const problemDetails = this.getProblemDetails(error.error);
                const individualApiError: IndividualApiError = {
                    status: error.status,
                    message: this.getErrorMessage(error, problemDetails, 'BySourceId'),
                    problemDetails,
                    originalError: error,
                };

                return throwError(() => individualApiError);
            }),
        );
    }

    getIndividualUdfs(guid: string): Observable<IndividualUdfField[]> {
        return this.http.get<unknown[] | null>(this.buildIndividualUdfsEndpoint(guid), {
            params: { page: 1, size: 100 },
        }).pipe(
            map((response) => this.projectIndividualUdfsResponse(response)),
            catchError((error: HttpErrorResponse) => {
                const problemDetails = this.getProblemDetails(error.error);
                const individualApiError: IndividualApiError = {
                    status: error.status,
                    message: this.getErrorMessage(error, problemDetails, 'Individual Udfs'),
                    problemDetails,
                    originalError: error,
                };

                return throwError(() => individualApiError);
            }),
        );
    }

    approveFpcIndividual(relationships: FpcRelationship[], isUpcomingCongress: boolean): Observable<void> {
        return this.http
            .post<void>(this.approveFpcIndividualEndpoint, relationships, {
                params: { IsUpcomingCongress: String(isUpcomingCongress) },
            })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    const problemDetails = this.getProblemDetails(error.error);
                    const individualApiError: IndividualApiError = {
                        status: error.status,
                        message: this.getErrorMessage(error, problemDetails, 'ApproveFPCIndividual'),
                        problemDetails,
                        originalError: error,
                    };

                    return throwError(() => individualApiError);
                }),
            );
    }

    rejectFpcIndividual(relationships: FpcRelationship[]): Observable<ApiResponse> {
        return this.http.post<ApiResponse>(this.rejectFpcIndividualEndpoint, relationships).pipe(
            catchError((error: HttpErrorResponse) => {
                const problemDetails = this.getProblemDetails(error.error);
                const individualApiError: IndividualApiError = {
                    status: error.status,
                    message: this.getErrorMessage(error, problemDetails, 'RejectFPCIndividual'),
                    problemDetails,
                    originalError: error,
                };

                return throwError(() => individualApiError);
            }),
        );
    }

    addIndividualNote(indGuid: string, note: IndividualNote): Observable<ApiResponse> {
        return this.http.post<ApiResponse>(this.buildIndividualNoteEndpoint(indGuid), note).pipe(
            catchError((error: HttpErrorResponse) => {
                const problemDetails = this.getProblemDetails(error.error);
                const individualApiError: IndividualApiError = {
                    status: error.status,
                    message: this.getErrorMessage(error, problemDetails, 'Individual Note'),
                    problemDetails,
                    originalError: error,
                };

                return throwError(() => individualApiError);
            }),
        );
    }

    getIndividualNote(indGuid: string, category: string, subCategory: string): Observable<Array<IndividualNote>> {
        return this.http.get<Array<IndividualNote>>(this.buildIndividualNotesEndpoint(indGuid), {
            params: { category, subCategory, page: 1, size: 100 },
        }).pipe(
            catchError((error: HttpErrorResponse) => {
                const problemDetails = this.getProblemDetails(error.error);
                const individualApiError: IndividualApiError = {
                    status: error.status,
                    message: this.getErrorMessage(error, problemDetails, 'Individual Note'),
                    problemDetails,
                    originalError: error,
                };

                return throwError(() => individualApiError);
            }),
        );
    }

    deleteIndividualAndEntityTypeLinksByLnkGuid(lnkGuids: string[]): Observable<void> {
        return this.http.put<void>(this.deleteIndividualAndEntityTypeLinksByLnkGuidEndpoint, lnkGuids).pipe(
            catchError((error: HttpErrorResponse) => {
                const problemDetails = this.getProblemDetails(error.error);
                const individualApiError: IndividualApiError = {
                    status: error.status,
                    message: this.getErrorMessage(error, problemDetails, 'DeleteIndividualAndEntityTypeLinksByLnkGuid'),
                    problemDetails,
                    originalError: error,
                };

                return throwError(() => individualApiError);
            }),
        );
    }

    updateFPCRelationshipByLnkGuidAndIndGuid(indGuid: string, lnkGuid: string, entityLinkType: string, removeFromFPCAppGroup: boolean = false): Observable<void> {
        return this.http.put<void>(this.updateFPCRelationshipByLnkGuidAndIndGuidEndpoint, null, {
            params: { indGuid, lnkGuid, entityLinkType, removeFromFPCAppGroup },
        }).pipe(
            catchError((error: HttpErrorResponse) => {
                const problemDetails = this.getProblemDetails(error.error);
                const individualApiError: IndividualApiError = {
                    status: error.status,
                    message: this.getErrorMessage(error, problemDetails, 'UpdateFPCRelationshipByLnkGuidAndIndGuid'),
                    problemDetails,
                    originalError: error,
                };

                return throwError(() => individualApiError);
            }),
        );
    }

    createRelationship(relationship: CreateRelationship): Observable<string> {
        return this.http.post<string>(`${this.apiUrl}/FPC/CreateRelationship`, relationship);
    }

    private buildLoggedInUdfEndpoint(): string {
        const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
        return `${trimmedApiUrl}/LoggedIn/Udf`;
    }

    private buildLoggedInGroupEndpoint(): string {
        const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
        return `${trimmedApiUrl}/LoggedIn/Group`;
    }

    private buildApproveFpcIndividualEndpoint(): string {
        const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
        return `${trimmedApiUrl}/FPC/ApproveFPCIndividual`;
    }

    private buildRejectFpcIndividualEndpoint(): string {
        const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
        return `${trimmedApiUrl}/FPC/RejectFPCIndividual`;
    }

    private buildDeleteIndividualAndEntityTypeLinksByLnkGuidEndpoint(): string {
        const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
        return `${trimmedApiUrl}/Individual/DeleteIndividualAndEntityTypeLinksByLnkGuid`;
    }

    private buildUpdateFPCRelationshipByLnkGuidAndIndGuidEndpoint(): string {
        const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
        return `${trimmedApiUrl}/FPC/UpdateFPCRelationshipByLnkGuidAndIndGuid`;
    }

    private buildBySourceIdEndpoint(sourceId: string): string {
        const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
        return `${trimmedApiUrl}/Individual/bysourceid/${sourceId}`;
    }

    private buildIndividualUdfsEndpoint(guid: string): string {
        const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
        return `${trimmedApiUrl}/Individual/${guid}/udfs`;
    }

    private buildIndividualNoteEndpoint(indGuid: string): string {
        const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
        return `${trimmedApiUrl}/Individual/${indGuid}/note`;
    }

    private buildIndividualNotesEndpoint(indGuid: string): string {
        const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
        return `${trimmedApiUrl}/Individual/${indGuid}/notes`;
    }

    private projectBySourceIdResponse(value: Record<string, unknown> | null): IndividualBySourceIdResponse {
        if (!value) {
            return {};
        }

        return {
            guid: this.asNullableString(value['guid']),
            sourceId: this.asNullableString(value['sourceId']),
            prefix: this.asNullableString(value['prefix']),
            firstName: this.asNullableString(value['firstName']),
            middleName: this.asNullableString(value['middleName']),
            lastName: this.asNullableString(value['lastName']),
        };
    }

    private projectIndividualUdfsResponse(value: unknown[] | null): IndividualUdfField[] {
        if (!Array.isArray(value)) {
            return [];
        }

        return value.flatMap((group) => {
            const groupRecord = this.asRecord(group);
            const fields = groupRecord['fields'];
            if (!Array.isArray(fields)) {
                return [];
            }

            return fields.map((field) => {
                const fieldRecord = this.asRecord(field);

                return {
                    fieldId: this.asNullableString(fieldRecord['fieldId']),
                    fieldName: this.asNullableString(fieldRecord['fieldName']),
                    value: this.asNullableString(fieldRecord['value']),
                    groupId: this.asNullableString(fieldRecord['groupId']),
                    groupName: this.asNullableString(fieldRecord['groupName']),
                };
            });
        });
    }

    private asRecord(value: unknown): Record<string, unknown> {
        if (!value || typeof value !== 'object') {
            return {};
        }

        return value as Record<string, unknown>;
    }

    private asNullableString(value: unknown): string | null | undefined {
        if (value === undefined) {
            return undefined;
        }

        if (value === null || typeof value === 'string') {
            return value;
        }

        return String(value);
    }

    private getProblemDetails(value: unknown): ProblemDetails | undefined {
        if (!value || typeof value !== 'object') {
            return undefined;
        }

        const candidate = value as ProblemDetails;
        if (
            'type' in candidate ||
            'title' in candidate ||
            'status' in candidate ||
            'detail' in candidate ||
            'instance' in candidate
        ) {
            return candidate;
        }

        return undefined;
    }

    private getErrorMessage(error: HttpErrorResponse, problemDetails?: ProblemDetails, endpointLabel: string = 'LoggedIn Udf'): string {
        if (problemDetails?.title) {
            return problemDetails.title;
        }

        if (error.status === 0) {
            return `Network error while calling Individual ${endpointLabel} endpoint.`;
        }

        return `Individual ${endpointLabel} request failed.`;
    }
}
